import { Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { formatRelativeTime } from "../lib/utils";
import { hasConvexConfig, hasWorkOsConfig } from "../lib/config";

export function DashboardPage() {
  if (!hasConvexConfig || !hasWorkOsConfig) {
    return (
      <div className="page">
        <h1>Dashboard</h1>
        <p className="lede">Configure WorkOS and Convex to load inventory.</p>
      </div>
    );
  }
  return <DashboardAuthenticated />;
}

function DashboardAuthenticated() {
  const data = useQuery(api.dashboard.overview);

  if (data === undefined) {
    return <p className="muted">Loading dashboard…</p>;
  }

  const { kpis, lowStock, awaitingReceive, openOrders, recentMovements } = data;

  return (
    <div className="page dashboard">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="lede">Inventory health, open purchase orders, and recent stock moves.</p>
        </div>
        <div className="header-actions">
          <Link className="btn" to="/inventory">
            Add item
          </Link>
          <Link className="btn primary" to="/orders/new">
            New order
          </Link>
        </div>
      </div>

      <div className="kpi-strip">
        <div className="kpi-card">
          <div className="kpi-label">SKUs</div>
          <div className="kpi-value">{kpis.skuCount}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">On hand</div>
          <div className="kpi-value">{kpis.onHandUnits}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">In transit</div>
          <div className="kpi-value">{kpis.inTransitUnits}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Low stock</div>
          <div className="kpi-value">{kpis.lowStockCount}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Awaiting receive</div>
          <div className="kpi-value">{kpis.awaitingReceiveCount}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Open orders</div>
          <div className="kpi-value">{kpis.openOrderCount}</div>
        </div>
      </div>

      <div className="dash-grid">
        <section className="panel">
          <h2 className="panel-title">Awaiting receive</h2>
          {awaitingReceive.length === 0 ? (
            <p className="muted tiny">No shipped orders waiting.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Supplier</th>
                  <th>Lines</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {awaitingReceive.map((o) => (
                  <tr key={o._id}>
                    <td>
                      <Link to={`/orders/${o._id}`}>{o.title}</Link>
                    </td>
                    <td>{o.supplier ?? "—"}</td>
                    <td>{o.rowCount}</td>
                    <td>{formatRelativeTime(o.lastActivityAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="panel">
          <h2 className="panel-title">Low stock</h2>
          {lowStock.length === 0 ? (
            <p className="muted tiny">No items below reorder point.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>On hand</th>
                  <th>Reorder</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.map((i) => (
                  <tr key={i._id}>
                    <td>
                      <div>{i.displayName}</div>
                      <div className="muted tiny mono">{i.ean ?? i.asin ?? ""}</div>
                    </td>
                    <td>{i.onHandQty}</td>
                    <td>{i.reorderPoint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="panel">
          <h2 className="panel-title">Open orders</h2>
          {openOrders.length === 0 ? (
            <p className="muted tiny">No open purchase orders.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Stage</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {openOrders.map((o) => (
                  <tr key={o._id}>
                    <td>
                      <Link to={`/orders/${o._id}`}>{o.title}</Link>
                    </td>
                    <td>
                      <span className={`stage-chip ${o.stage}`}>{o.stage}</span>
                    </td>
                    <td>{formatRelativeTime(o.lastActivityAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="panel">
          <h2 className="panel-title">Recent movements</h2>
          {recentMovements.length === 0 ? (
            <p className="muted tiny">No stock movements yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Qty</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {recentMovements.map((m) => (
                  <tr key={m._id}>
                    <td>
                      {m.kind}
                      {m.notes ? <div className="muted tiny">{m.notes}</div> : null}
                    </td>
                    <td>{m.quantity}</td>
                    <td>{formatRelativeTime(m.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}
